import { useEffect, useRef, useState } from 'react';
import { X, RefreshCw, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EditUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  cards?: Array<{ slug: string; shortCode: string; orgSlug?: string | null }>;
}

interface CardData {
  personal: Record<string, string>;
  contact: Record<string, string | boolean>;
  social: Record<string, string>;
  theme?: Record<string, unknown>;
  images?: Record<string, string>;
  links?: unknown[];
  privacy?: Record<string, boolean>;
}

interface FetchedCard {
  slug: string;
  userId: string;
  data: CardData;
}

type EditMode = 'uniform' | 'individual';

interface FieldDef {
  key: string;
  label: string;
  section: 'personal' | 'contact' | 'social';
  placeholder?: string;
}

// ─── Field definitions ────────────────────────────────────────────────────────

const FIELDS: FieldDef[] = [
  // Personal
  { key: 'firstName',  label: 'First name',   section: 'personal', placeholder: 'Jane' },
  { key: 'lastName',   label: 'Last name',    section: 'personal', placeholder: 'Smith' },
  { key: 'title',      label: 'Job title',    section: 'personal', placeholder: 'Marketing Manager' },
  { key: 'company',    label: 'Company',      section: 'personal', placeholder: 'Acme Inc.' },
  { key: 'location',   label: 'Location',     section: 'personal', placeholder: 'Berlin, Germany' },
  { key: 'bio',        label: 'Bio',          section: 'personal', placeholder: 'Short bio…' },
  // Contact
  { key: 'phone',             label: 'Phone',       section: 'contact', placeholder: '+49 30 12345678' },
  { key: 'phoneSecondary',    label: 'Phone (2nd)', section: 'contact', placeholder: '+49 30 99999999' },
  { key: 'website',           label: 'Website',     section: 'contact', placeholder: 'https://example.com' },
  { key: 'addressStreet',     label: 'Street',      section: 'contact', placeholder: 'Musterstr. 1' },
  { key: 'addressPostalCode', label: 'Postal code', section: 'contact', placeholder: '10115' },
  { key: 'addressCity',       label: 'City',        section: 'contact', placeholder: 'Berlin' },
  { key: 'addressCountry',    label: 'Country',     section: 'contact', placeholder: 'Germany' },
  // Social
  { key: 'linkedin',  label: 'LinkedIn',  section: 'social', placeholder: 'https://linkedin.com/in/…' },
  { key: 'twitter',   label: 'Twitter/X', section: 'social', placeholder: 'https://twitter.com/…' },
  { key: 'instagram', label: 'Instagram', section: 'social', placeholder: 'https://instagram.com/…' },
  { key: 'github',    label: 'GitHub',    section: 'social', placeholder: 'https://github.com/…' },
];

const SECTION_LABELS: Record<string, string> = {
  personal: 'Personal',
  contact: 'Contact',
  social: 'Social',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFieldValue(data: CardData | undefined, field: FieldDef): string {
  if (!data) return '';
  const section = data[field.section] as Record<string, unknown> | undefined;
  if (!section) return '';
  return String(section[field.key] ?? '');
}

// ─── FieldRow ─────────────────────────────────────────────────────────────────

interface FieldRowProps {
  field: FieldDef;
  mode: EditMode;
  onModeChange: (mode: EditMode) => void;
  uniformValue: string;
  onUniformChange: (val: string) => void;
  individualValues: Record<string, string>;
  onIndividualChange: (userId: string, val: string) => void;
  users: EditUser[];
  fetchedCards: Record<string, FetchedCard | null>;
}

function FieldRow({
  field,
  mode,
  onModeChange,
  uniformValue,
  onUniformChange,
  individualValues,
  onIndividualChange,
  users,
  fetchedCards,
}: FieldRowProps) {
  const inputBase =
    'block w-full rounded-lg bg-panel border border-line px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';

  return (
    <div className="grid grid-cols-[160px_152px_1fr] gap-3 items-start py-2.5 border-b border-line last:border-0">
      {/* Label */}
      <span className="text-xs font-medium text-ink-muted pt-2">{field.label}</span>

      {/* Mode toggle */}
      <div className="flex rounded-lg overflow-hidden border border-line text-xs h-8">
        {(['uniform', 'individual'] as EditMode[]).map((m) => (
          <button
            key={m}
            onClick={() => onModeChange(m)}
            className={`flex-1 capitalize transition-colors ${
              mode === m
                ? 'bg-accent-600 text-white'
                : 'bg-panel text-ink-muted hover:bg-raised'
            }`}
          >
            {m === 'uniform' ? 'Same' : 'Individual'}
          </button>
        ))}
      </div>

      {/* Input area */}
      <div>
        {mode === 'uniform' ? (
          <input
            type="text"
            value={uniformValue}
            onChange={(e) => onUniformChange(e.target.value)}
            placeholder={field.placeholder ?? ''}
            className={inputBase}
          />
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
            {users.map((u) => {
              const hasCard = fetchedCards[u.id] != null;
              return (
                <div key={u.id} className="flex items-center gap-2">
                  <span
                    className={`shrink-0 text-xs w-24 truncate ${
                      hasCard ? 'text-ink-muted' : 'text-ink-faint'
                    }`}
                    title={u.email}
                  >
                    {u.firstName || u.email.split('@')[0]}
                  </span>
                  <input
                    type="text"
                    value={individualValues[u.id] ?? ''}
                    onChange={(e) => onIndividualChange(u.id, e.target.value)}
                    placeholder={hasCard ? (field.placeholder ?? '') : 'No card'}
                    disabled={!hasCard}
                    className={`${inputBase} flex-1 ${!hasCard ? 'opacity-40 cursor-not-allowed' : ''}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface MultiCardEditProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: EditUser[];
  onSaved: () => void;
}

export default function MultiCardEdit({
  isOpen,
  onClose,
  selectedUsers,
  onSaved,
}: MultiCardEditProps) {
  const [fetchedCards, setFetchedCards] = useState<Record<string, FetchedCard | null>>({});
  const [loadingCards, setLoadingCards] = useState(false);

  const [modes, setModes] = useState<Record<string, EditMode>>({});
  const [uniformValues, setUniformValues] = useState<Record<string, string>>({});
  const [individualValues, setIndividualValues] = useState<Record<string, Record<string, string>>>({});

  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ saved: number; failed: number } | null>(null);
  const [saveError, setSaveError] = useState('');

  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Reset & fetch when opened ──────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setResult(null);
    setSaveError('');
    setLoadingCards(true);

    const initModes: Record<string, EditMode> = {};
    const initIndividual: Record<string, Record<string, string>> = {};
    FIELDS.forEach((f) => {
      initModes[f.key] = 'individual';
      initIndividual[f.key] = {};
    });
    setModes(initModes);
    setUniformValues({});
    setIndividualValues(initIndividual);

    async function fetchAll() {
      const results: Record<string, FetchedCard | null> = {};
      await Promise.all(
        selectedUsers.map(async (u) => {
          const slug = u.cards?.[0]?.slug;
          if (!slug) { results[u.id] = null; return; }
          try {
            const resp = await fetch(`/api/admin/cards/${slug}?userId=${u.id}`, {
              credentials: 'include',
            });
            if (!resp.ok) { results[u.id] = null; return; }
            const json = await resp.json() as { slug: string; userId: string; data: CardData };
            results[u.id] = { slug: json.slug, userId: json.userId, data: json.data };
          } catch {
            results[u.id] = null;
          }
        })
      );
      setFetchedCards(results);

      // Pre-fill individual values
      setIndividualValues((prev) => {
        const next = { ...prev };
        FIELDS.forEach((f) => {
          next[f.key] = { ...(next[f.key] ?? {}) };
          selectedUsers.forEach((u) => {
            const card = results[u.id];
            const row = next[f.key];
            if (card && row) row[u.id] = getFieldValue(card.data, f);
          });
        });
        return next;
      });

      setLoadingCards(false);
    }

    void fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Mode toggle ────────────────────────────────────────────────────────────
  function handleModeChange(fieldKey: string, newMode: EditMode) {
    setModes((prev) => ({ ...prev, [fieldKey]: newMode }));
    if (newMode === 'uniform') {
      const field = FIELDS.find((f) => f.key === fieldKey)!;
      const firstUser = selectedUsers.find((u) => fetchedCards[u.id]);
      const seedVal = firstUser ? getFieldValue(fetchedCards[firstUser.id]?.data, field) : '';
      setUniformValues((prev) => ({
        ...prev,
        [fieldKey]: prev[fieldKey] !== undefined ? prev[fieldKey] : seedVal,
      }));
    }
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true);
    setSaveError('');
    setResult(null);

    let saved = 0;
    let failed = 0;

    await Promise.all(
      selectedUsers.map(async (u) => {
        const card = fetchedCards[u.id];
        if (!card) return;

        const merged: CardData = JSON.parse(JSON.stringify(card.data)) as CardData;

        for (const field of FIELDS) {
          const newVal =
            modes[field.key] === 'uniform'
              ? (uniformValues[field.key] ?? '')
              : (individualValues[field.key]?.[u.id] ?? '');

          if (newVal.trim() === '') continue;
          const section = merged[field.section] as Record<string, unknown>;
          section[field.key] = newVal;
        }

        try {
          const resp = await fetch(`/api/cards/${card.slug}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...merged, userId: u.id }),
          });
          if (resp.ok) saved++;
          else failed++;
        } catch {
          failed++;
        }
      })
    );

    setSaving(false);
    setResult({ saved, failed });
    if (failed === 0 && saved > 0) {
      onSaved();
      setTimeout(onClose, 1200);
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const usersWithCards = selectedUsers.filter((u) => fetchedCards[u.id] != null);
  const usersWithoutCards = selectedUsers.filter(
    (u) => !loadingCards && fetchedCards[u.id] == null
  );
  const sections = ['personal', 'contact', 'social'] as const;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl bg-panel border border-line shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-line shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-ink">
              Edit cards —{' '}
              <span className="text-accent-600 dark:text-accent-400">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}
              </span>
            </h2>
            {!loadingCards && usersWithoutCards.length > 0 && (
              <p className="inline-flex items-center gap-1 text-xs text-warning-600 dark:text-warning-400 mt-0.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {usersWithoutCards.length} user{usersWithoutCards.length !== 1 ? 's' : ''} have no card and will be skipped
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-ink-muted hover:text-ink hover:bg-raised transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4">
          {loadingCards ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-accent-500" />
              <p className="text-sm text-ink-muted">Loading card data…</p>
            </div>
          ) : (
            <div className="space-y-8">
              {sections.map((section) => {
                const sectionFields = FIELDS.filter((f) => f.section === section);
                return (
                  <div key={section}>
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-muted">
                        {SECTION_LABELS[section]}
                      </h3>
                      <div className="flex-1 h-px bg-panel" />
                    </div>

                    {/* Column headers */}
                    <div className="grid grid-cols-[160px_152px_1fr] gap-3 pb-1 mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-ink-faint">Field</span>
                      <span className="text-[10px] uppercase tracking-wider text-ink-faint">Mode</span>
                      <span className="text-[10px] uppercase tracking-wider text-ink-faint">Value</span>
                    </div>

                    <div className="rounded-xl border border-line bg-raised px-4">
                      {sectionFields.map((field) => (
                        <FieldRow
                          key={field.key}
                          field={field}
                          mode={modes[field.key] ?? 'individual'}
                          onModeChange={(m) => handleModeChange(field.key, m)}
                          uniformValue={uniformValues[field.key] ?? ''}
                          onUniformChange={(val) =>
                            setUniformValues((prev) => ({ ...prev, [field.key]: val }))
                          }
                          individualValues={individualValues[field.key] ?? {}}
                          onIndividualChange={(userId, val) =>
                            setIndividualValues((prev) => ({
                              ...prev,
                              [field.key]: { ...prev[field.key], [userId]: val },
                            }))
                          }
                          users={selectedUsers}
                          fetchedCards={fetchedCards}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-line shrink-0">
          <div className="text-xs text-ink-muted">
            {!loadingCards && (
              <>
                <span className="text-ink font-medium">{usersWithCards.length}</span>
                {' of '}{selectedUsers.length} users have cards
                {' · '}Empty fields are skipped (existing value kept)
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {result && (
              <div
                className={`flex items-center gap-1.5 text-xs font-medium ${
                  result.failed === 0
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-danger-600 dark:text-danger-400'
                }`}
              >
                {result.failed === 0 ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5" />
                )}
                {result.saved} saved{result.failed > 0 ? `, ${result.failed} failed` : ''}
              </div>
            )}
            {saveError && <span className="text-xs text-danger-500">{saveError}</span>}

            <button
              onClick={onClose}
              className="rounded-lg border border-line bg-panel px-4 py-2 text-sm text-ink hover:bg-raised hover:text-ink transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={saving || loadingCards || usersWithCards.length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {saving && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
              Save{' '}
              {usersWithCards.length > 0
                ? `${usersWithCards.length} card${usersWithCards.length !== 1 ? 's' : ''}`
                : 'cards'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
